// Vendor Program v2 PR 3 — VFI Accounting dashboard strip. Absorbs the
// retired VfiDashboardView tile layout (same /vendor-invoices/dashboard
// endpoint) and adds the two PR 3 tiles: "Reports outstanding"
// (expected-vs-filed compliance; links down to the review queue) and
// "Avg rating" (borrower sign-off rollup; em-dash when no sign-offs yet).

import { Card, Skeleton } from "@/design-system/components/ui";
import { useVfiDashboard, useVfiRatingRollup } from "@/hooks/portfolioAdmin/useVendorBilling";
import { useVendorReportCompliance } from "@/hooks/portfolioAdmin/useVendorReports";
import { fmtMoney } from "../billing/vendorBillingFormat";

interface VfiSummaryTilesProps {
  /** Scrolls the page to the "Reports awaiting review" queue. */
  onGoToReportsQueue: () => void;
}

export function VfiSummaryTiles({ onGoToReportsQueue }: VfiSummaryTilesProps) {
  const dashboardQuery = useVfiDashboard();
  const complianceQuery = useVendorReportCompliance();
  const ratingQuery = useVfiRatingRollup();

  if (dashboardQuery.isLoading) {
    return (
      <section
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3"
        data-testid="vfi-tiles-loading"
      >
        {Array.from({ length: 7 }, (_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </section>
    );
  }
  if (dashboardQuery.isError || !dashboardQuery.data) {
    return (
      <Card data-testid="vfi-tiles-error">
        <p className="text-small text-status-error m-0" role="alert">
          Could not load the VFI dashboard. Refresh to retry.
        </p>
      </Card>
    );
  }

  const data = dashboardQuery.data;
  const outstandingReports = complianceQuery.data?.outstanding.length;
  const rating = ratingQuery.data;

  return (
    <section
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-space-3"
      data-testid="vfi-dashboard-tiles"
    >
      <SummaryTile
        label="Outstanding receivables"
        value={fmtMoney(data.outstandingTotal)}
        delta={`${data.openCount} open invoice${data.openCount === 1 ? "" : "s"}`}
      />
      <SummaryTile
        label="In transit"
        value={fmtMoney(data.inTransitTotal)}
        delta={`${data.inTransitCount} processing`}
        tone={data.inTransitCount > 0 ? "info" : undefined}
      />
      <SummaryTile
        label="Paid YTD"
        value={fmtMoney(data.paidYtd)}
        delta={`${data.paidYtdCount} invoice${data.paidYtdCount === 1 ? "" : "s"}`}
      />
      <SummaryTile
        label="Overdue 30+"
        value={fmtMoney(
          data.overdueBuckets
            .filter((b) => b.label !== "1–29 days")
            .reduce((sum, b) => sum + b.amount, 0),
        )}
        tone="error"
      />
      <SummaryTile
        label="Overdue 90+"
        value={fmtMoney(
          data.overdueBuckets.find((b) => b.label === "90+ days")?.amount ?? 0,
        )}
        tone="error"
      />
      <SummaryTile
        label="Reports outstanding"
        value={outstandingReports != null ? String(outstandingReports) : "—"}
        tone={outstandingReports ? "error" : undefined}
        delta={
          complianceQuery.data
            ? `${complianceQuery.data.filedCount} of ${complianceQuery.data.expectedCount} filed`
            : undefined
        }
        action={
          <button
            type="button"
            onClick={onGoToReportsQueue}
            className="self-start text-caption text-gold-shadow underline hover:text-onyx"
            data-testid="vfi-tile-reports-link"
          >
            View review queue
          </button>
        }
        testId="vfi-tile-reports-outstanding"
      />
      <SummaryTile
        label="Avg rating"
        value={
          rating?.averageOverall != null
            ? `${rating.averageOverall.toFixed(1)} / 4`
            : "—"
        }
        delta={
          rating
            ? `${rating.signOffCount} sign-off${rating.signOffCount === 1 ? "" : "s"}`
            : undefined
        }
        testId="vfi-tile-avg-rating"
      />
    </section>
  );
}

interface SummaryTileProps {
  label: string;
  value: string;
  delta?: string;
  tone?: "error" | "info";
  action?: React.ReactNode;
  testId?: string;
}

function SummaryTile({ label, value, delta, tone, action, testId }: SummaryTileProps) {
  const toneClass =
    tone === "error" ? "text-status-error" : tone === "info" ? "text-status-info" : "";
  return (
    <Card className="flex flex-col gap-space-1" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
        {label}
      </span>
      <span className={`font-display font-bold text-h2 tabular-nums ${toneClass}`}>
        {value}
      </span>
      {delta ? <span className="text-small text-gray-mid">{delta}</span> : null}
      {action}
    </Card>
  );
}
